import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";

import { FormattedNumber } from "../FormattedNumber/FormattedNumber";
import { getSavedPersonInfo } from "../../reducers/selectors";
import { showToast } from "../../utils/helper";
import { getBankAccountBalance } from "../../services/api/accounts";
import { Icons } from "../UI/Icon/Icon";
import { CircularProgress } from "../UI/CircularProgress/CircularProgress";

import style from "./BankAccountsSelector.scss";

const defaultFilters = {
  currencyISO: "",
  amount: 0,
};

export const BankAccountsSelector = ({ bankAccounts, setAllBankAccountAreInvalid = () => {}, ...props }) => {
  const [validBankAccounts, setValidBankAccounts] = useState({});

  useEffect(() => {
    let allInvalid = true;
    if (props.filters.amount == 0) allInvalid = false;
    Object.values(validBankAccounts).forEach((key) => {
      if (key) allInvalid = false;
    });

    setAllBankAccountAreInvalid(allInvalid);
  }, [bankAccounts, validBankAccounts]);

  const setBankAccountIsValid = (PersonBankAccountNumber, valid) => {
    setValidBankAccounts((prev) => ({ ...prev, [PersonBankAccountNumber]: valid }));
  };

  return (
    <div className={style.wrapper}>
      {bankAccounts
        .filter((account) => account.PersonBankAccountStatus === "Active")
        .map((account, index) => (
          <BankAccountItem
            bankAccountItem={account}
            index={index}
            setBankAccountIsValid={setBankAccountIsValid}
            {...props}
          />
        ))}
    </div>
  );
};

const BankAccountItem = ({
  bankAccountItem,
  selectedBankAccount,
  onSelectBankAccount = () => {},
  filters = defaultFilters,
  disabled = false,
  loading,
  index,
  setBankAccountIsValid = () => {},
}) => {
  const [balance, setBalance] = useState(0);
  const [notEnoughFunds, setNotEnoughFunds] = useState(true);
  const [isCurrency, setIsCurrency] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const personInfo = useSelector(getSavedPersonInfo);

  useEffect(() => {
    setNotEnoughFunds(() => (filters.amount && +filters.amount > +balance) || balance == 0);
    setIsCurrency(
      () => filters.currencyISO && bankAccountItem.PersonBankAccountCurrencyISO !== filters.currencyISO
    );
  }, [filters, balance, bankAccountItem]);

  useEffect(() => {
    setBankAccountIsValid(bankAccountItem.PersonBankAccountNumber, !isCurrency && !notEnoughFunds);
  }, [bankAccountItem, isCurrency, notEnoughFunds]);

  useEffect(() => {
    setLoadingBalance(() => true);
    getBankAccountBalance({ bankAccountNumber: bankAccountItem.PersonBankAccountNumber })
      .then(({ response }) => {
        if (response !== undefined) {
          setBalance(() => response.balance);
        } else {
          showToast("Error obteniendo los datos de la cuenta del servidor", true);
        }
      })
      .catch((err) => {
        console.error(err);
        showToast("Error obteniendo los datos de la cuenta del servidor", true);
      })
      .finally(() => {
        setLoadingBalance(() => false);
      });
  }, [bankAccountItem]);

  return (
    <div
      className={`${style.bankAccountItem} ${
        selectedBankAccount &&
        selectedBankAccount.PersonBankAccountNumber === bankAccountItem.PersonBankAccountNumber
          ? style.active
          : ""
      } ${notEnoughFunds || isCurrency || disabled ? style.disabled : null}`}
      key={index}
      onClick={() => {
        if (!loading && !isCurrency && !disabled && !notEnoughFunds) onSelectBankAccount(bankAccountItem);
      }}
    >
      <div className={style.row}>
        <div>{`${personInfo.PhysicalPersonFirstName} ${personInfo.PhysicalPersonSecondName} ${personInfo.PhysicalPersonFirstLastName} ${personInfo.PhysicalPersonSecondLastName}`}</div>
        <span>
          {selectedBankAccount ? (
            selectedBankAccount.PersonBankAccountNumber === bankAccountItem.PersonBankAccountNumber ? (
              <Icons.RadioCheckedBlackIcon size="sm" />
            ) : (
              <Icons.RadioUncheckedBlackIcon size="sm" />
            )
          ) : (
            <Icons.RadioUncheckedBlackIcon size="sm" />
          )}
        </span>
      </div>
      <hr />
      <span
        className={`${style.row} ${style.balance} ${
          notEnoughFunds || isCurrency || disabled ? style.disabled : ""
        }`}
      >
        <p> Nº cuenta: {bankAccountItem.PersonBankAccountNumber}</p>
        <p>
          <span className={style.balanceLoader}>
            {`Saldo: `}
            {loadingBalance ? (
              <CircularProgress sm />
            ) : (
              <Fragment>
                <FormattedNumber
                  value={balance}
                  minimumFractionDigits={bankAccountItem.PersonBankAccountCurrencyISO == "USD" ? 2 : 0}
                  maximumFractionDigits={bankAccountItem.PersonBankAccountCurrencyISO == "USD" ? 2 : 0}
                />
                {` ${bankAccountItem.PersonBankAccountCurrencyISO}`}
              </Fragment>
            )}
          </span>
        </p>
      </span>
    </div>
  );
};
